"use client";
import { useEffect, useState } from "react";
import Link from "next/link";
import Head from "next/head";

const TermsOfService = () => {
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const total = Math.max(
        document.documentElement.scrollHeight - window.innerHeight,
        1
      );
      setScrollProgress((window.scrollY / total) * 100);
    };

    const updateScrollPadding = () => {
      document.documentElement.style.scrollPaddingTop = `50px`; // breathing room
    };

    updateScrollPadding();
    handleScroll();

    window.addEventListener("resize", updateScrollPadding);
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("resize", updateScrollPadding);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const toc = [
    { label: "Introduction", href: "#s-0" },
    { label: "1) Eligibility & capacity", href: "#s-1" },
    { label: "2) Changes to these Terms", href: "#s-2" },
    { label: "3) What Infinitcode AI does", href: "#s-3" },
    { label: "4) Accounts & integrations", href: "#s-4" },
    { label: "5) Lawful use", href: "#s-5" },
    { label: "6) LLMs & data transfers", href: "#s-6" },
    { label: "7) Confidentiality & code", href: "#s-7" },
    { label: "8) User Content & Output", href: "#s-8" },
    { label: "9) Intellectual Property", href: "#s-9" },
    { label: "10) Billing & 14-day right", href: "#s-10" },
    { label: "11) Third-party services", href: "#s-11" },
    { label: "12) Privacy (Albania)", href: "#s-12" },
    { label: "13) Service changes", href: "#s-13" },
    { label: "14) Warranties", href: "#s-14" },
    { label: "15) Liability", href: "#s-15" },
    { label: "16) Indemnity (business)", href: "#s-16" },
    { label: "17) Marketing references", href: "#s-17" },
    { label: "18) Disputes & venue", href: "#s-18" },
    { label: "19) Governing law", href: "#s-19" },
    { label: "20) Survival", href: "#s-20" },
    { label: "21) Assignment", href: "#s-21" },
    { label: "22) Waiver & severability", href: "#s-22" },
    { label: "23) Cross-border & sanctions", href: "#s-23" },
    { label: "24) E-contracts & notices", href: "#s-24" },
    { label: "25) Albania consumer info", href: "#s-25" },
    { label: "26) Entire agreement", href: "#s-26" },
  ];

  return (
    <>
      <Head>
        <title>Terms of Service - Infinitcode</title>
        <meta
          name="description"
          content="Terms of Service for Infinitcode — please read these terms carefully before using our Services."
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Head>

      {/* Styles */}
      <style jsx>{`
        @property --angle {
          syntax: "<angle>";
          initial-value: 0deg;
          inherits: false;
        }
        .animated-border {
          position: relative;
          background: linear-gradient(#041536, #041536) padding-box,
            conic-gradient(
                from var(--angle),
                transparent 0%,
                transparent 45%,
                #3bcf8e 50%,
                transparent 55%,
                transparent 100%
              )
              border-box;
          border: 2px solid transparent;
          border-radius: 1rem;
          animation: spin 8s linear infinite;
        }
        .glow-effect {
          position: absolute;
          inset: -15px;
          background: conic-gradient(
            from var(--angle),
            transparent 0%,
            transparent 45%,
            #3bcf8e 50%,
            transparent 55%,
            transparent 100%
          );
          border-radius: 1.5rem;
          filter: blur(15px);
          opacity: 0.3;
          animation: spin 8s linear infinite;
          z-index: -1;
          pointer-events: none;
        }
        @keyframes spin {
          from { --angle: 0deg; }
          to { --angle: 360deg; }
        }
        @supports not (background: conic-gradient(from var(--angle), red, blue)) {
          .animated-border { background: #041536; border: none; overflow: hidden; }
          .animated-border::before {
            content: "";
            position: absolute; inset: -2px;
            background: conic-gradient(
              from 0deg at 50% 50%,
              transparent 0%,
              transparent 45%,
              #3bcf8e 50%,
              transparent 55%,
              transparent 100%
            );
            border-radius: inherit; z-index: -1; animation: rotateFallback 8s linear infinite;
          }
          .animated-border::after { content: ""; position: absolute; inset: 2px; background: #041536; border-radius: calc(1rem - 2px); z-index: -1; }
          .glow-effect { background: conic-gradient(from 0deg at 50% 50%, transparent 0%, transparent 45%, #3bcf8e 50%, transparent 55%, transparent 100%); animation: rotateFallback 8s linear infinite; }
          @keyframes rotateFallback { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        }
        .floating-shapes { position: fixed; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; z-index: -1; }
        .shape { position: absolute; opacity: 0.1; animation: float 20s infinite linear; }
        .shape:nth-child(1) { top: 25%; left: 12%; animation-delay: 0s; }
        .shape:nth-child(2) { top: 65%; right: 12%; animation-delay: -10s; }
        .shape:nth-child(3) { bottom: 25%; left: 22%; animation-delay: -5s; }
        @keyframes float { 0%, 100% { transform: translateY(0) rotate(0); } 50% { transform: translateY(-20px) rotate(180deg); } }
        .section-divider { width: 100%; height: 1px; background: linear-gradient(90deg, transparent, #3bcf8e, transparent); margin: 2rem 0; }
        ::-webkit-scrollbar { width: 8px; }
        ::-webkit-scrollbar-track { background: #041536; }
        ::-webkit-scrollbar-thumb { background: linear-gradient(180deg, #3bcf8e, #2da771); border-radius: 4px; }
        ::-webkit-scrollbar-thumb:hover { background: linear-gradient(180deg, #2da771, #3bcf8e); }
      `}</style>

      <div className="bg-gradient-to-br from-[#020F2A] via-[#041536] to-[#020F2A] text-white min-h-screen">
        {/* Scroll Progress */}
        <div
          className="fixed top-0 left-0 h-1 bg-gradient-to-r from-[#3BCF8E] to-[#2DA771] z-50 transition-all duration-100 ease-out"
          style={{ width: `${scrollProgress}%` }}
        />

        {/* Floating Background Shapes */}
        <div className="floating-shapes">
          <div className="shape w-24 h-24 bg-gradient-to-r from-[#3BCF8E]/20 to-[#2DA771]/20 rounded-full" />
          <div className="shape w-16 h-16 bg-gradient-to-r from-[#2DA771]/20 to-[#3BCF8E]/20 rounded-lg rotate-45" />
          <div className="shape w-12 h-12 bg-[#3BCF8E]/20 rounded-full" />
        </div>

        {/* Header */}
        <header className="relative overflow-hidden">
          <div className="max-w-4xl mx-auto px-6 py-16">
            {/* Back Button */}
            <div className="mb-8">
              <Link
                href="/"
                className="inline-flex items-center gap-2 text-gray-300 hover:text-[#3BCF8E] transition-all duration-300 group"
              >
                <svg
                  className="w-5 h-5 transform group-hover:-translate-x-1 transition-transform duration-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="font-medium">Back to Home</span>
              </Link>
            </div>

            <div className="text-center">
              <div className="relative">
                <div className="glow-effect" />
                <div className="animated-border p-12 bg-gradient-to-br from-[#041536]/90 to-[#020F2A]/90">
                  <h1 className="text-5xl md:text-6xl font-bold mb-4">
                    Terms of{" "}
                    <span className="bg-gradient-to-r from-[#3BCF8E] to-[#2DA771] text-transparent bg-clip-text">
                      Service
                    </span>
                  </h1>
                  <div className="text-lg text-left flex items-center gap-10 justify-center text-gray-300">
                    <p>
                      <span className="text-[#3BCF8E] font-semibold">Updated:</span> March 13, 2025
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick nav */}
            <div className="max-w-4xl mx-auto px-6 mt-8">
              <div className="flex flex-wrap gap-2 text-sm">
                {toc.map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    className="px-3 py-1 rounded-full border border-[#3BCF8E]/30 text-gray-300 hover:text-white hover:border-[#3BCF8E] transition"
                  >
                    {item.label}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-4xl mx-auto px-6 pb-16">
          {/* Introduction */}
          <section id="s-0" className="mb-12">
            <div className="bg-gradient-to-br from-[#041536]/60 to-[#020F2A]/60 backdrop-blur-lg rounded-2xl p-8 border border-[#3BCF8E]/20">
              <h2 className="text-3xl font-bold mb-6 text-[#3BCF8E]">Introduction</h2>
              <div className="text-gray-200 leading-relaxed space-y-4">
                <p><strong>Legal entity:</strong> Infinitcode sh.p.k. (“Infinitcode AI”, “we”, “us”)</p>
                <p><strong>Registered address:</strong> Tirane, Tirane Njesia Nr. 2, Rr. Qemal Stafa, Pallati 118, Shkalla 1, Ap. 18, Tirane, Albania, 1000</p>
                <p><strong>Business registry:</strong> National Business Center (QKB)</p>
                <p><strong>NIPT:</strong> M51825024H</p>
                <p><strong>Email:</strong> <Link href="mailto:hello@infinitcode.com" className="text-[#3BCF8E] hover:text-[#2DA771]">hello@infinitcode.com</Link></p>
                <p>
                  These Terms (the “Terms”) are a contract between you (“you”, “User”) and Infinitcode sh.p.k.
                  They govern your access to <Link href="https://infinitcode.ai" className="text-[#3BCF8E] hover:text-[#2DA771]">https://infinitcode.ai</Link> (the “Website”),
                  our web application (the “App”), and all related features and services (the “Services”). By using the Services you accept these Terms. If you do not agree, do not use the Services.
                </p>
                <p>
                  <em>Regulatory note (Albania):</em> We comply with Law no. 10128/2009 “On Electronic Commerce” (as amended) (the “E-Commerce Law”), which requires clear provider identification and contract terms online. qbz.gov.al aida.gov.al
                </p>
              </div>
            </div>
          </section>

          <div className="section-divider" />

          {/* 1 */}
          <section id="s-1" className="mb-12">
            <div className="bg-gradient-to-br from-[#041536]/40 to-[#020F2A]/40 rounded-2xl p-8 border border-[#3BCF8E]/10">
              <h2 className="text-2xl font-bold mb-4 text-[#3BCF8E]">1) Eligibility & capacity</h2>
              <p className="text-gray-200">
                You must have legal capacity to contract. Under Albanian Civil Code, full capacity is acquired at 18; minors 14–17 generally need a legal representative’s prior consent. We do not offer accounts to persons under 16. If you register on behalf of a company, you confirm you’re authorized to bind that company.
              </p>
            </div>
          </section>

          {/* 2 */}
          <section id="s-2" className="mb-12">
            <div className="bg-gradient-to-r from-[#3BCF8E]/10 to-[#2DA771]/10 rounded-2xl p-8 border border-[#3BCF8E]/30">
              <h2 className="text-2xl font-bold mb-4 text-[#3BCF8E]">2) Changes to these Terms</h2>
              <p className="text-gray-200">
                We may update these Terms by posting a new version on the Website. Changes take effect on the stated effective date; material changes will be signaled reasonably in advance on the Website. (E-Commerce providers must keep contractual information accessible and clear.) qbz.gov.al
              </p>
            </div>
          </section>

          {/* 3 */}
          <section id="s-3" className="mb-12">
            <div className="bg-gradient-to-br from-[#041536]/40 to-[#020F2A]/40 rounded-2xl p-8 border border-[#3BCF8E]/10">
              <h2 className="text-2xl font-bold mb-4 text-[#3BCF8E]">3) What Infinitcode AI does</h2>
              <p className="text-gray-200">
                Infinitcode AI provides AI-assisted code review and developer tooling. We analyze your pull requests to find bugs, performance issues, and propose fixes.
              </p>
            </div>
          </section>

          {/* 4 */}
          <section id="s-4" className="mb-12">
            <div className="bg-gradient-to-br from-[#041536]/40 to-[#020F2A]/40 rounded-2xl p-8 border border-[#3BCF8E]/10">
              <h2 className="text-2xl font-bold mb-4 text-[#3BCF8E]">4) Accounts & Third-Party integrations</h2>
              <div className="text-gray-200 space-y-4">
                <p>
                  To register you connect a GitHub or GitLab account (“Third-Party Account”). You authorize us to access permitted data (e.g., profile, email, repos/org metadata) to deliver the Services. You control scopes in the third-party settings. Keep credentials secure; you’re responsible for actions under your account.
                </p>
                <p><strong>Authorized Users.</strong> If you provision access to team members, you’re responsible for their use and compliance.</p>
              </div>
            </div>
          </section>

          {/* 5 */}
          <section id="s-5" className="mb-12">
            <div className="bg-gradient-to-r from-[#3BCF8E]/10 to-[#2DA771]/10 rounded-2xl p-8 border border-[#3BCF8E]/30">
              <h2 className="text-2xl font-bold mb-4 text-[#3BCF8E]">5) Lawful use (don’t be the villain)</h2>
              <div className="text-gray-200 space-y-4">
                <p>Use the Services only as permitted by law and these Terms. No abuse, no malware, no scraping, no denial-of-service. Do not infringe third-party IP or privacy. Don’t try to bypass technical restrictions.</p>
              </div>
            </div>
          </section>

          {/* 6 */}
          <section id="s-6" className="mb-12">
            <div className="bg-gradient-to-br from-[#041536]/40 to-[#020F2A]/40 rounded-2xl p-8 border border-[#3BCF8E]/10">
              <h2 className="text-2xl font-bold mb-4 text-[#3BCF8E]">6) AI/LLM providers & data transfers</h2>
              <div className="text-gray-200 space-y-4">
                <p>We integrate with model providers (currently OpenAI and DeepSeek) via API.</p>
                <p><strong>Roles.</strong> For your repository/code and related personal data you submit, you are the controller. We act as your processor and our model vendors act as our sub-processors.</p>
                <p>
                  <strong>Transfers.</strong> Personal data may be processed outside Albania. Under Albania’s Data Protection Law no. 124/2024, cross-border transfers require an adequacy decision or transfer tools (e.g., SCCs/BCRs) and safeguards. We use appropriate transfer mechanisms or instructions published by the Commissioner.
                </p>
                <p><strong>Children’s data.</strong> If a data subject is under 16, online-service consent must be given/authorized by a parent/guardian. Do not submit such data to us unless you have that consent.</p>
                <p><strong>Training use.</strong><br/>
                  OpenAI: we disable provider training by contract/config where available.<br/>
                  DeepSeek: if you enable DeepSeek, you acknowledge DeepSeek may use API-submitted data to improve its models per DeepSeek’s terms; do not send any personal data without a valid legal basis and proper notice to data subjects. (Albanian law requires a lawful basis and valid transfer safeguards.)
                </p>
                <p>We’ll list current sub-processors in our Privacy Policy and notify of material changes where required.</p>
              </div>
            </div>
          </section>

          {/* 7 */}
          <section id="s-7" className="mb-12">
            <div className="bg-gradient-to-r from-[#3BCF8E]/10 to-[#2DA771]/10 rounded-2xl p-8 border border-[#3BCF8E]/30">
              <h2 className="text-2xl font-bold mb-4 text-[#3BCF8E]">7) Confidentiality & code handling</h2>
              <p className="text-gray-200">
                We do not clone your repos; we process PR diffs/metadata necessary for review. Your proprietary code remains confidential. We will not use your code to train our own models. (Consumer and e-commerce rules demand clarity on key contract info and confidentiality.) qbz.gov.al
              </p>
            </div>
          </section>

          {/* 8 */}
          <section id="s-8" className="mb-12">
            <div className="bg-gradient-to-br from-[#041536]/40 to-[#020F2A]/40 rounded-2xl p-8 border border-[#3BCF8E]/10">
              <h2 className="text-2xl font-bold mb-4 text-[#3BCF8E]">8) User Content & Output</h2>
              <div className="text-gray-200 space-y-4">
                <p>“User Content” is what you supply (code, comments, repo metadata). “Output” is what our Services generate for you.</p>
                <ul className="list-disc ml-6 space-y-1">
                  <li>You grant us a license to process User Content to provide and improve the Services.</li>
                  <li>We assign to you our rights (if any) in the specific Output produced for you. Similar prompts may produce similar output for others.</li>
                </ul>
                <p>You warrant you have rights to the User Content and that its use with our Services won’t breach law, third-party rights, or third-party platform terms.</p>
              </div>
            </div>
          </section>

          {/* 9 */}
          <section id="s-9" className="mb-12">
            <div className="bg-gradient-to-r from-[#3BCF8E]/10 to-[#2DA771]/10 rounded-2xl p-8 border border-[#3BCF8E]/30">
              <h2 className="text-2xl font-bold mb-4 text-[#3BCF8E]">9) Intellectual Property</h2>
              <p className="text-gray-200">
                The Services (software, UI, documentation, branding) are our IP or our licensors’. We grant you a revocable, non-transferable, non-sublicensable license to use the Services for your internal business purposes. Don’t copy, modify, reverse-engineer, or create competing services.
              </p>
            </div>
          </section>

          {/* 10 */}
          <section id="s-10" className="mb-12">
            <div className="bg-gradient-to-br from-[#041536]/40 to-[#020F2A]/40 rounded-2xl p-8 border border-[#3BCF8E]/10">
              <h2 className="text-2xl font-bold mb-4 text-[#3BCF8E]">10) Purchases, subscriptions, billing, and the consumer 14-day right</h2>
              <div className="text-gray-200 space-y-4">
                <p><strong>Plans.</strong> You may buy monthly or annual subscriptions, priced per seat. Pricing: <Link href="https://infinitcode.ai/pricing" className="text-[#3BCF8E] hover:text-[#2DA771]">https://infinitcode.ai/pricing</Link>. Payment is processed by our payment partners (e.g., Paddle). Taxes/VAT are added where applicable.</p>
                <p><strong>Auto-renewal.</strong> Subscriptions renew automatically unless you cancel before the next billing date.</p>
                <p><strong>Right of withdrawal (consumers only).</strong> Albanian distance-contract rules grant a 14-day withdrawal right from the day of contract conclusion, with a standard model form and refund of payments received (minus any proportionate amount for services you expressly asked us to start before withdrawal). You lose the withdrawal right if you expressly request immediate performance and acknowledge loss of the right once performance is complete within the 14 days.</p>
                <p><strong>How to cancel or withdraw.</strong><br/>Cancel auto-renewal any time in the App, effective next cycle.<br/>To exercise the 14-day consumer withdrawal right, email <Link href="mailto:hello@infinitcode.com" className="text-[#3BCF8E] hover:text-[#2DA771]">hello@infinitcode.com</Link> with your account email and “Withdrawal” in the subject; we’ll provide the model form and instructions.</p>
                <p><strong>Refunds.</strong> We issue refunds as required by law and otherwise at our discretion where stated.</p>
              </div>
            </div>
          </section>

          {/* 11 */}
          <section id="s-11" className="mb-12">
            <div className="bg-gradient-to-r from-[#3BCF8E]/10 to-[#2DA771]/10 rounded-2xl p-8 border border-[#3BCF8E]/30">
              <h2 className="text-2xl font-bold mb-4 text-[#3BCF8E]">11) Third-party services</h2>
              <p className="text-gray-200">Links, plugins, and materials from third parties (e.g., GitHub/GitLab) are governed by their own terms. We’re not responsible for third-party sites or services.</p>
            </div>
          </section>

          {/* 12 */}
          <section id="s-12" className="mb-12">
            <div className="bg-gradient-to-br from-[#041536]/40 to-[#020F2A]/40 rounded-2xl p-8 border border-[#3BCF8E]/10">
              <h2 className="text-2xl font-bold mb-4 text-[#3BCF8E]">12) Privacy & data protection (Albania)</h2>
              <p className="text-gray-200">
                Our processing is described in our Privacy Policy. Data subjects in Albania have rights to access, rectification, erasure, restriction, portability (where applicable), and objection, per Law no. 124/2024. Supervisory authority: Komisioneri për të Drejtën e Informimit dhe Mbrojtjen e të Dhënave Personale (IDP). You can complain to IDP or to a court. International transfers rely on adequacy, SCCs, BCRs or other lawful bases under Albanian law and any decisions/instructions of the Commissioner.
              </p>
            </div>
          </section>

          {/* 13 */}
          <section id="s-13" className="mb-12">
            <div className="bg-gradient-to-r from-[#3BCF8E]/10 to-[#2DA771]/10 rounded-2xl p-8 border border-[#3BCF8E]/30">
              <h2 className="text-2xl font-bold mb-4 text-[#3BCF8E]">13) Service changes</h2>
              <p className="text-gray-200">
                We may change, suspend, or discontinue features at any time. If we materially reduce paid features during a prepaid term, we’ll provide an appropriate remedy (e.g., pro-rata credit or ability to terminate early).
              </p>
            </div>
          </section>

          {/* 14 */}
          <section id="s-14" className="mb-12">
            <div className="bg-gradient-to-br from-[#041536]/40 to-[#020F2A]/40 rounded-2xl p-8 border border-[#3BCF8E]/10">
              <h2 className="text-2xl font-bold mb-4 text-[#3BCF8E]">14) Warranties — what we don’t promise</h2>
              <p className="text-gray-200">
                The Services are provided “as is” and “as available.” We don’t warrant uninterrupted or error-free operation or fitness for your specific purposes. This does not affect non-waivable statutory consumer rights (unfair terms are void to that extent).
              </p>
            </div>
          </section>

          {/* 15 */}
          <section id="s-15" className="mb-12">
            <div className="bg-gradient-to-r from-[#3BCF8E]/10 to-[#2DA771]/10 rounded-2xl p-8 border border-[#3BCF8E]/30">
              <h2 className="text-2xl font-bold mb-4 text-[#3BCF8E]">15) Liability</h2>
              <div className="text-gray-200 space-y-4">
                <p>We do not exclude liability where it would be unlawful to do so (e.g., for death or personal injury caused by negligence, or willful misconduct).</p>
                <p><strong>Business users:</strong> To the maximum extent permitted by law, our aggregate liability for all claims in any 6-month period is limited to the fees you paid us in that period.</p>
                <p><strong>Consumers:</strong> We will be liable for losses you suffer that are a foreseeable result of our breach or negligence, but not for business losses.</p>
                <p>Any term that would inappropriately limit mandatory consumer rights is ineffective to that extent.</p>
              </div>
            </div>
          </section>

          {/* 16 */}
          <section id="s-16" className="mb-12">
            <div className="bg-gradient-to-br from-[#041536]/40 to-[#020F2A]/40 rounded-2xl p-8 border border-[#3BCF8E]/10">
              <h2 className="text-2xl font-bold mb-4 text-[#3BCF8E]">16) Indemnity (business users only)</h2>
              <p className="text-gray-200">
                If you use the Services for business, you’ll indemnify us from third-party claims arising from your unlawful use or your breach of third-party rights. This clause does not apply to consumers, as such a term may be unfair.
              </p>
            </div>
          </section>

          {/* 17 */}
          <section id="s-17" className="mb-12">
            <div className="bg-gradient-to-r from-[#3BCF8E]/10 to-[#2DA771]/10 rounded-2xl p-8 border border-[#3BCF8E]/30">
              <h2 className="text-2xl font-bold mb-4 text-[#3BCF8E]">17) Marketing references</h2>
              <p className="text-gray-200">
                We may identify your organization (name/logo) as a customer, unless you opt out in writing. We’ll follow any brand-use guidelines you provide.
              </p>
            </div>
          </section>

          {/* 18 */}
          <section id="s-18" className="mb-12">
            <div className="bg-gradient-to-br from-[#041536]/40 to-[#020F2A]/40 rounded-2xl p-8 border border-[#3BCF8E]/10">
              <h2 className="text-2xl font-bold mb-4 text-[#3BCF8E]">18) Disputes, mediation, and venue</h2>
              <div className="text-gray-200 space-y-4">
                <p>We prefer to resolve issues amicably.</p>
                <p><strong>Mediation (optional):</strong> Either party may propose mediation under Albanian mediation rules.</p>
                <p><strong>Arbitration (optional, business-to-business):</strong> If both parties agree after a dispute arises, it may be referred to arbitration seated in Albania under Law no. 52/2023 “On Arbitration in the Republic of Albania.”</p>
                <p><strong>Consumers:</strong> Requiring consumers to take disputes exclusively to arbitration may be an unfair term. Consumers always retain the right to go to court.</p>
                <p><strong>Courts:</strong> Failing mediation/arbitration, courts of Tirana have jurisdiction, and Albanian law applies (see §19).</p>
              </div>
            </div>
          </section>

          {/* 19 */}
          <section id="s-19" className="mb-12">
            <div className="bg-gradient-to-r from-[#3BCF8E]/10 to-[#2DA771]/10 rounded-2xl p-8 border border-[#3BCF8E]/30">
              <h2 className="text-2xl font-bold mb-4 text-[#3BCF8E]">19) Governing law</h2>
              <p className="text-gray-200">These Terms and non-contractual obligations are governed by the laws of the Republic of Albania.</p>
            </div>
          </section>

          {/* 20 */}
          <section id="s-20" className="mb-12">
            <div className="bg-gradient-to-br from-[#041536]/40 to-[#020F2A]/40 rounded-2xl p-8 border border-[#3BCF8E]/10">
              <h2 className="text-2xl font-bold mb-4 text-[#3BCF8E]">20) Survival</h2>
              <p className="text-gray-200">
                Sections that by their nature should survive termination (e.g., IP, confidentiality, payments due, limitations of liability) survive.
              </p>
            </div>
          </section>

          {/* 21 */}
          <section id="s-21" className="mb-12">
            <div className="bg-gradient-to-r from-[#3BCF8E]/10 to-[#2DA771]/10 rounded-2xl p-8 border border-[#3BCF8E]/30">
              <h2 className="text-2xl font-bold mb-4 text-[#3BCF8E]">21) Assignment</h2>
              <p className="text-gray-200">
                You may not assign your rights or obligations without our prior written consent. We may assign to an affiliate or in connection with a merger, sale, or reorganization.
              </p>
            </div>
          </section>

          {/* 22 */}
          <section id="s-22" className="mb-12">
            <div className="bg-gradient-to-br from-[#041536]/40 to-[#020F2A]/40 rounded-2xl p-8 border border-[#3BCF8E]/10">
              <h2 className="text-2xl font-bold mb-4 text-[#3BCF8E]">22) Waiver & severability</h2>
              <p className="text-gray-200">
                No waiver is implied by silence or delay. If any term is invalid, the rest remains in force; the invalid part is replaced by a valid term closest to the original intent.
              </p>
            </div>
          </section>

          {/* 23 */}
          <section id="s-23" className="mb-12">
            <div className="bg-gradient-to-r from-[#3BCF8E]/10 to-[#2DA771]/10 rounded-2xl p-8 border border-[#3BCF8E]/30">
              <h2 className="text-2xl font-bold mb-4 text-[#3BCF8E]">23) Cross-border access & sanctions</h2>
              <p className="text-gray-200">
                You must comply with applicable sanctions and export-control laws (Albania and other relevant jurisdictions). Do not use the Services if such laws prohibit your access.
              </p>
            </div>
          </section>

          {/* 24 */}
          <section id="s-24" className="mb-12">
            <div className="bg-gradient-to-br from-[#041536]/40 to-[#020F2A]/40 rounded-2xl p-8 border border-[#3BCF8E]/10">
              <h2 className="text-2xl font-bold mb-4 text-[#3BCF8E]">24) Electronic contracts & notices</h2>
              <p className="text-gray-200">
                You consent to electronic records and signatures. Albanian law recognizes electronic signatures and records with legal effect when statutory conditions are met.
              </p>
            </div>
          </section>

          {/* 25 */}
          <section id="s-25" className="mb-12">
            <div className="bg-gradient-to-r from-[#3BCF8E]/10 to-[#2DA771]/10 rounded-2xl p-8 border border-[#3BCF8E]/30">
              <h2 className="text-2xl font-bold mb-4 text-[#3BCF8E]">25) Consumers in Albania — extra information</h2>
              <ul className="text-gray-200 list-disc ml-6 space-y-2">
                <li><strong>Service provider:</strong> Infinitcode sh.p.k., address and NIPT as above; contact: <Link href="mailto:hello@infinitcode.com" className="text-[#3BCF8E] hover:text-[#2DA771]">hello@infinitcode.com</Link>.</li>
                <li><strong>Main characteristics & pricing:</strong> Described on the Website before purchase; all taxes/fees disclosed prior to checkout.</li>
                <li><strong>Complaint channels:</strong> You can email us, or submit a complaint to the Consumer Protection Commission pursuant to Law no. 9902/2008 (unfair terms, distance contracts).</li>
              </ul>
            </div>
          </section>

          {/* 26 */}
          <section id="s-26" className="mb-4">
            <div className="bg-gradient-to-r from-[#3BCF8E]/10 to-[#2DA771]/10 rounded-2xl p-8 border border-[#3BCF8E]/30">
              <h2 className="text-2xl font-bold mb-4 text-[#3BCF8E]">26) Entire agreement</h2>
              <p className="text-gray-200">
                These Terms plus the Privacy Policy are the entire agreement about the Services and supersede prior understandings.
              </p>
            </div>
          </section>
        </main>
      </div>
    </>
  );
};

export default TermsOfService;
